import React, { FC, useEffect } from 'react'
import { useFormikContext } from 'formik'
import { Stack, Typography } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import type { ChangeInstallationOwnerFormData } from './types'
import { DatePicker, TextField } from 'src/Libs/inputs/formik'
import { UsersSearch } from 'src/Libs/user/components'
import { SearchFieldBaseOption } from 'src/Libs/inputs/formik/SearchFieldBase'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import { Installation } from 'src/Libs/installations/models'
import { isFlowTheme } from 'src/Libs/utils'

interface FormProps {
  onChangeFormValid: (isFormValid: boolean) => void
  installation?: Installation
  maxDate: Moment
  minDate: Moment
}

/**
 * Displays form for change installation owner modal
 * @param onChangeFormValid - function to change form valid state
 */
const Form: FC<FormProps> = ({ onChangeFormValid, installation, maxDate, minDate }) => {
  const { t } = useCustomTranslation()
  const { isValid, setFieldValue } = useFormikContext<ChangeInstallationOwnerFormData>()
  const E3DC_USER_FILTER = `user.level=USER,user.source=e3dc`
  const FLOW_USER_FILTER = 'user.level=USER,user.source=hager'
  const userFilter = isFlowTheme ? FLOW_USER_FILTER : E3DC_USER_FILTER

  const onChangeUser = (value: SearchFieldBaseOption | null): void => {
    setFieldValue('userId', value?.value ?? undefined)
  }

  useEffect(() => {
    onChangeFormValid(isValid)
  }, [isValid, onChangeFormValid])

  return (
    <form noValidate>
      <Stack spacing={4}>
        <UsersSearch
          name={'userId'}
          label={t('installation.details.info.change-access-owner.user-field-placeholder')}
          maxDisplayedResultCount={10}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onValueChange={onChangeUser}
          additionalFilter={userFilter}
        />
        <DatePicker
          name={'startAt'}
          label={t('installation.details.info.change-access-owner.start-at-field')}
          required
          maxDate={maxDate}
          minDate={minDate}
        />
        <Typography variant="subtitle2">{t('installation.details.info.change-access-owner.reason-field-text')}</Typography>
        <TextField label={t('installation.details.info.change-access-owner.reason-field-title')} name={'reason'} multiline withTrim />
      </Stack>
    </form>
  )
}

export default Form
